"use client";

import { startTransition, useEffect, useState } from "react";
import { trackSEOIntentCaptured } from "@/lib/analytics";
import { safeSessionGet, safeSessionSet } from "@/lib/browser-storage";
import { getSEOIntentConfig, normalizeSEOIntent, type SEOIntentConfig } from "@/lib/seo-intents";
import type { CapturedSEOEntry } from "./KundaliFormCard.parts";

type SEOIntentParams = ReturnType<typeof readSEOIntentParams>;

export function useCapturedSEOEntry(seoEntryOverride: CapturedSEOEntry | null | undefined) {
  const [capturedSeoEntry, setCapturedSeoEntry] = useState<CapturedSEOEntry | null>(null);
  useEffect(() => {
    if (seoEntryOverride !== undefined || typeof window === "undefined") return;
    const captureIntent = () => captureSEOIntent(setCapturedSeoEntry);
    captureIntent();
    window.addEventListener("seo-intent-refresh", captureIntent);
    return () => window.removeEventListener("seo-intent-refresh", captureIntent);
  }, [seoEntryOverride]);
  return capturedSeoEntry;
}

function captureSEOIntent(setCapturedSeoEntry: (entry: CapturedSEOEntry) => void) {
  try {
    const params = readSEOIntentParams();
    const intent = normalizeSEOIntent(params.intent || safeSessionGet("seo_entry_intent"));
    if (shouldSkipGenericIntent(intent, params.intent)) return;

    const config = getSEOIntentConfig(intent);
    const sourcePath =
      params.source || safeSessionGet("seo_entry_path") || document.referrer || "direct";
    const effectivePrompt = params.prompt && params.prompt.trim() ? params.prompt : config.prompt;
    persistSEOIntent(config, sourcePath, effectivePrompt, params);
    const effectiveConfig = params.prompt ? { ...config, prompt: effectivePrompt } : config;
    startTransition(() => setCapturedSeoEntry({ config: effectiveConfig, sourcePath }));
    trackCapturedSEOIntent(config, sourcePath, params);
  } catch {
    // Keep the form usable when query parsing or storage is blocked.
  }
}

function readSEOIntentParams() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const getParam = (key: string) => params.get(key) || hashParams.get(key) || undefined;
  return {
    intent: getParam("intent"),
    source: getParam("source"),
    prompt: getParam("prompt") || safeSessionGet("seo_entry_prompt") || undefined,
    topic: getParam("topic") || safeSessionGet("seo_entry_topic") || undefined,
    auto: getParam("auto"),
  };
}

function shouldSkipGenericIntent(intent: string, explicitIntent?: string) {
  return intent === "generic" && !explicitIntent && !safeSessionGet("seo_entry_intent");
}

function persistSEOIntent(
  config: SEOIntentConfig,
  sourcePath: string,
  effectivePrompt: string,
  params: SEOIntentParams
) {
  safeSessionSet("seo_entry_intent", config.intent);
  safeSessionSet("seo_entry_path", sourcePath);
  safeSessionSet("seo_entry_page_category", config.pageCategory);
  safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
  safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
  safeSessionSet("seo_entry_prompt", effectivePrompt);
  if (params.topic) safeSessionSet("seo_entry_topic", params.topic);
  if (params.auto === "1") safeSessionSet("seo_entry_auto_send", "1");
}

function trackCapturedSEOIntent(
  config: SEOIntentConfig,
  sourcePath: string,
  params: SEOIntentParams
) {
  trackSEOIntentCaptured({
    source_path: sourcePath,
    intent: config.intent,
    page_category: config.pageCategory,
    target: "free-kundali",
    ...(params.topic ? { topic_key: params.topic } : {}),
    ...(params.prompt ? { chip_prompt: true } : {}),
  });
}
